import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, MessageCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_amazonia-trips-1/artifacts/gy2z1mzk_ayahuascatrip%20logo.png";

const navItems = [
  { label: 'Experience', href: '#experience' },
  { label: 'Guide', href: '#guide' },
  { label: 'Packages', href: '#packages' },
  { label: 'Safety', href: '#safety' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'FAQ', href: '#faq' },
];

export const Navigation = ({ whatsappLink }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-jungle-dark/95 backdrop-blur-md' : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="Ayahuasca Trip"
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="nav-link text-sm"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* WhatsApp CTA */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white px-5 py-2.5 rounded-full font-heading font-semibold text-sm uppercase tracking-wider transition-transform duration-300 hover:scale-105"
            data-testid="nav-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Contact Us
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-jungle-dark/98 backdrop-blur-md border-t border-neon-cyan/20"
            data-testid="mobile-menu"
          >
            <div className="py-4 space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="block px-4 py-3 text-white font-heading uppercase tracking-wider hover:text-neon-cyan hover:bg-white/5 transition-colors duration-200"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 mx-4 mt-4 bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-3 rounded-full font-heading font-semibold text-sm uppercase tracking-wider justify-center"
                data-testid="mobile-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Contact Us
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};
